import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Landing.css';
import PublicNavigation from '../components/PublicNavigation';

const PhoneMock = () => (
  <svg viewBox="0 0 300 600" xmlns="http://www.w3.org/2000/svg" className="mock-svg">
    <rect x="10" y="10" rx="36" ry="36" width="280" height="580" fill="#111827" />
    <rect x="25" y="70" rx="12" width="250" height="495" fill="#ffffff" />
    <rect x="120" y="28" rx="6" ry="6" width="60" height="10" fill="#374151" />
    <circle cx="150" cy="52" r="6" fill="#374151" />
    {/* header */}
    <rect x="25" y="70" width="250" height="48" fill="#f3f4f6" />
    <rect x="36" y="85" width="120" height="16" rx="4" fill="#9ca3af" />
    {/* cards */}
    <rect x="40" y="140" width="220" height="80" rx="10" fill="#e5e7eb" />
    <rect x="40" y="235" width="220" height="80" rx="10" fill="#e5e7eb" />
    <rect x="40" y="330" width="220" height="80" rx="10" fill="#e5e7eb" />
    {/* button */}
    <rect x="90" y="430" width="120" height="36" rx="8" fill="#3b82f6" />
  </svg>
);

const Landing = () => {
  const location = useLocation();
  const [expandedFeature, setExpandedFeature] = useState(null);

  const featureItems = useMemo(
    () => [
      {
        id: 'show-leagues-map',
        title: 'Discover leagues on an interactive map',
        image: '/LandingPage/Show%20Leagues%20-%20Map.png',
        description:
          'Filter by league type, status, sport, skill range, and proximity. Navigate the live map with hoverable markers, then flip to the list view when you want a quick comparison of nearby leagues.',
      },
      {
        id: 'show-leagues-list',
        title: 'List view with powerful filters',
        image: '/LandingPage/Show%20Leagues%20-%20List.png',
        description:
          'Switch to the list view to sort and filter leagues by status, distance, and skill level. Clean cards, action buttons, and context badges help players understand requirements at a glance.',
      },
      {
        id: 'match-formats',
        title: 'Serve up multiple match formats',
        image: '/LandingPage/Match%20formats.png',
        description:
          'Offer best-of-three, timed sets, or custom race-to scores in the same season. Admins define formats per league or division, and players always see the rules before they step on court.',
      },
      {
        id: 'join-leagues',
        title: 'Join leagues in a click',
        image: '/LandingPage/Join%20Leagues.png',
        description:
          'Review requirements, express interest, and monitor approval in seconds. Admins see the same view with instant accept or decline actions to keep seasons moving.',
      },
      {
        id: 'subleagues',
        title: 'Automatic subleague grading',
        image: '/LandingPage/Subleagues.png',
        description:
          'Playthlon auto-builds divisions based on player level, presenting clear tiers and direct links into each subleague’s standings, matches, and roster.',
      },
      {
        id: 'see-rankings',
        title: 'Live standings and leaderboards',
        image: '/LandingPage/See%20rankings.png',
        description:
          'Track wins, losses, game differential, and points in real time. Table layouts stay consistent across devices so players follow progress week after week.',
      },
      {
        id: 'stats-history',
        title: 'Player timelines and stats',
        image: '/LandingPage/Full%20History.png',
        description:
          'Every result feeds a searchable history timeline, backed by detailed analytics that show how each match shifts level and momentum.',
      },
      {
        id: 'stats-level',
        title: 'Spot levels at a glance',
        image: '/LandingPage/Stats%20and%20Level.png',
        description:
          'Dashboards highlight current ratings, movement trends, and confidence intervals so admins guide promotions while players track their climb.',
      },
      {
        id: 'continuation-leagues',
        title: 'Follow on leagues in one click',
        image: '/LandingPage/Continuation.png',
        description:
          'Wrap a season, hit "Create follow on league," and Playthlon clones the settings, re-invites the right players, and rebalances subleagues using last season\'s results. No spreadsheets, no rebuilds.',
      },
    ],
    []
  );

  useEffect(() => {
    const targetFromHash = location.hash ? location.hash.replace('#', '') : null;
    const targetFromState = location.state && location.state.targetSection
      ? location.state.targetSection
      : null;
    const targetId = targetFromHash || targetFromState;

    if (targetId) {
      const scrollToElement = () => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      // Delay slightly to ensure sections are rendered
      if (document.readyState === 'complete') {
        scrollToElement();
      } else {
        window.addEventListener('load', scrollToElement, { once: true });
      }

      if (!location.hash && targetFromState) {
        window.history.replaceState({}, '', `#${targetId}`);
      }
    }
  }, [location]);

  useEffect(() => {
    if (!expandedFeature) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setExpandedFeature(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedFeature]);

  return (
    <div className="landing">
      <PublicNavigation />

      <section className="hero" id="hero">
        <div className="hero-text">
          <h1>Run box leagues and tournaments with ease</h1>
          <p>Create superleagues, auto-split by levels, track matches and player progress. Simple for admins, delightful for players.</p>
          <div className="hero-cta">
            <Link to="/signup" className="btn-primary">Get started</Link>
            <Link to="/login" className="btn-secondary">I already have an account</Link>
          </div>
        </div>
        <div className="hero-art">
          <PhoneMock />
        </div>
      </section>

      <section className="features" id="features">
        <h2>Everything you need to run thriving leagues</h2>
        <div className="feature-grid">
          <div className="feature-card">
            <h3>Launch multi-division seasons</h3>
            <p>Spin up a superleague, set start and end dates, and invite players in minutes. Playthlon handles the structure so you can focus on the community.</p>
          </div>
          <div className="feature-card">
            <h3>Balanced competition, automatically</h3>
            <p>Players are graded by level and seeded into subleagues that keep matches close. Everyone still sees the bigger picture via cross-division rankings.</p>
          </div>
          <div className="feature-card">
            <h3>Scores that build real insight</h3>
            <p>Match results update standings, history timelines, and player ratings instantly. Admins get drag-and-drop management tools, players get clean summaries.</p>
          </div>
        </div>
      </section>

      <section className="feature-gallery" id="tour">
        <h2>See Playthlon in action</h2>
        <p className="feature-gallery-intro">These in-app screenshots show the exact experiences players and admins get on day one.</p>
        <div className="feature-gallery-grid">
          {featureItems.map((feature) => (
            <article
              key={feature.id}
              className="feature-item"
              onClick={() => setExpandedFeature(feature)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setExpandedFeature(feature);
                }
              }}
            >
              <div className="feature-item-image">
                <img src={feature.image} alt={feature.title} loading="lazy" />
              </div>
              <div className="feature-item-content">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {expandedFeature && (
        <div className="feature-modal" onClick={() => setExpandedFeature(null)}>
          <div className="feature-modal-card">
            <div className="feature-modal-image">
              <img src={expandedFeature.image} alt={expandedFeature.title} loading="lazy" />
            </div>
            <div className="feature-modal-content">
              <h3>{expandedFeature.title}</h3>
              <p>{expandedFeature.description}</p>
              <p className="feature-modal-hint">Click anywhere to close</p>
            </div>
          </div>
        </div>
      )}

      <section className="faq" id="faq">
        <h2>FAQ</h2>
        <div className="faq-list">
          <details>
            <summary>Can I run both leagues and knockout tournaments?</summary>
            <div>
              <p>Standard box leagues and multi division leagues are already available. Tournament (knockout) style will be added very soon.</p>
            </div>
          </details>
          <details>
            <summary>How are player levels calculated?</summary>
            <div>
              <p>Levels are powered by a damped Elo system: favourites gain a few tenths of a point for expected wins, while upsets deliver larger—but capped—boosts to the underdog. The nearer someone gets to level 0 or 100, the stronger the damping, keeping ratings realistic and stable across seasons.</p>
              <p>Every user's history can be seen through the profile page stats and plots, providing a clear audit trail for the numbers you see on the dashboard.</p>
            </div>
          </details>
          <details>
            <summary>Can players see other subleague results?</summary>
            <div>
              <p>Yes. Players always see live standings for their own division plus the other subleagues in the same superleague. That means new rivalries start before promotions even happen. Admins get extra controls to drill into each division from the Subleagues screen.</p>
            </div>
          </details>
          <details>
            <summary>Do we support multiple match formats?</summary>
            <div>
              <p>Currently, we support single-set match formats. Players see the chosen format on every match card and result entry, so there's never confusion about how to play or report scores. Additional formats will be added in future updates.</p>
            </div>
          </details>
          <details>
            <summary>What sports are supported?</summary>
            <div>
              <p>Currently singles tennis is supported, but we're planning to expand to all single and team ball sports soon.</p>
            </div>
          </details>
          <details>
            <summary>How does signup and onboarding work?</summary>
            <div>
              <p>Players can create an account with email or Google and immediately browse public leagues. When they hit "Join Leagues," their interest lands in the admin queue where you can accept, reject, or message them before the season starts.</p>
            </div>
          </details>
          <details>
            <summary>Do admins get the same interface as players?</summary>
            <div>
              <p>Admins see everything players do, plus management tabs for members, match approvals, and league settings. The admin view includes action buttons, filters, and a toggle to switch between map and list modes.</p>
            </div>
          </details>
        </div>
      </section>

      <footer className="landing-footer">
        <div>© {new Date().getFullYear()} Playthlon</div>
        <div className="footer-links">
          <a href="#features">Features</a>
          <a href="#tour">Screenshots</a>
          <a href="#faq">FAQ</a>
          <Link to="/login">Login</Link>
        </div>
      </footer>
    </div>
  );
};

export default Landing;


